let idToken = "";
let groups: string[] = [];

export type AccountRef = { id: string; label: string };

/** The reserved account meaning "every account, merged". Read-only server-side. */
export const ALL_ACCOUNTS = "all";

let accountList: AccountRef[] = [];
let selected = "";

export function setAccounts(list: AccountRef[]): void {
  accountList = list;
  if (!selected || !isKnown(selected)) selected = list[0]?.id ?? "";
}

export const accounts = (): AccountRef[] => accountList;
export const currentAccount = (): string => selected;

export const isKnown = (id: string): boolean =>
  id === ALL_ACCOUNTS || accountList.some((a) => a.id === id);

/** Silently ignores an unknown id: a stale link must not select nothing. */
export function setCurrentAccount(id: string): void {
  if (isKnown(id)) selected = id;
}

/**
 * Every request goes through here. A path built by hand is a path that reads the
 * wrong account the day someone forgets the prefix.
 *
 * `account` overrides the picker, and actions from the aggregate view MUST pass it:
 * the row knows which account it came from, and posting to "all" is refused.
 */
export const apiPath = (rest: string, account?: string): string =>
  rest.replace(/^\/api\//, `/api/${account ?? selected}/`);

export function setToken(t: string): void {
  idToken = t;
  groups = groupsFromToken(t);
}

export function clearToken(): void {
  idToken = "";
  groups = [];
}

/** Thrown on a 401 so callers can offer a new sign-in rather than print a string. */
export class SessionExpiredError extends Error {
  constructor() { super("Your session expired."); this.name = "SessionExpiredError"; }
}

/** The JWT payload, or null if this is not a token we can read. */
function claims(t: string): Record<string, unknown> | null {
  try {
    const part = t.split(".")[1];
    if (!part) return null;
    return JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return null;
  }
}

/**
 * Cognito ID tokens last an hour, and a stored one is routinely older than that
 * — a tab left open over lunch, or reopened the next morning. Checking `exp`
 * before the first call turns that into the sign-in screen instead of a failed
 * render. Unreadable or `exp`-less counts as expired: the only cost is one
 * sign-in, and the alternative is sending a token that cannot work.
 *
 * The 30s skew keeps a token that expires mid-flight from being sent at all.
 */
export function tokenExpired(t: string): boolean {
  const exp = claims(t)?.exp;
  if (typeof exp !== "number") return true;
  return exp * 1000 <= Date.now() + 30_000;
}

/**
 * Reads cognito:groups out of the ID token for UI purposes ONLY — deciding
 * whether to offer a button. It is NOT a security boundary: the token is in the
 * browser and trivially editable. Every write and the audit read are gated
 * server-side in api-actions.ts, which fails closed.
 *
 * API Gateway flattens array claims to "[a b]"; the raw JWT has a real array,
 * but handle both so this does not depend on which side produced it.
 */
function groupsFromToken(t: string): string[] {
  const raw = claims(t)?.["cognito:groups"];
  if (Array.isArray(raw)) return raw.map(String);
  if (typeof raw === "string") {
    return raw.replace(/^\s*\[/, "").replace(/\]\s*$/, "").split(/[\s,]+/).filter(Boolean);
  }
  return [];
}

export const isOperator = (): boolean => groups.includes("guardrails-operators");

async function call(method: string, path: string, body?: unknown): Promise<any> {
  const res = await fetch(path, {
    method,
    headers: {
      authorization: `Bearer ${idToken}`,
      ...(body ? { "content-type": "application/json" } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  // Drop the dead token here, not just in the caller. Leaving it in place was
  // the whole bug: the page said "reload to sign in again", the reload found
  // the same expired token in sessionStorage and signed straight back in with
  // it, and the only escape was closing the tab.
  if (res.status === 401) { clearToken(); throw new SessionExpiredError(); }
  if (res.status === 403) throw new Error("You are not a guardrails operator.");
  if (!res.ok) throw new Error(`${method} ${path} failed: ${res.status}`);
  return res.json();
}

/**
 * The account prefix is applied HERE, not by callers. Every existing call site keeps
 * passing the un-prefixed path it always passed, and none of them can forget.
 */
export const getJSON = (p: string, account?: string) => call("GET", apiPath(p, account));
export const postJSON = (p: string, b: unknown, account?: string) =>
  call("POST", apiPath(p, account), b);
